import React from "react";
import { Link } from "react-router-dom";
import { Images, BedDouble, LogOut, ArrowRight, FolderOpen, Compass, Map, MessageSquareQuote, Tag, Type, Headphones, CloudDownload, HardDrive, ImagePlus, Database, Route as RouteIcon } from "lucide-react";
import AdminAuthGate, { clearAdminPassword } from "@/components/admin/AdminAuthGate";

const DEPARTMENTS = [
  {
    key: "routes",
    title: "Routes directory",
    description:
      "Every URL the app serves — auto-generated from the routing config. Copy any link or open it in a new tab. Stays perfectly in sync with the codebase; no manual maintenance.",
    href: "/admin/routes",
    icon: RouteIcon,
    cta: "Open department",
    testid: "admin-dept-routes",
  },
  {
    key: "hotels",
    title: "Hotels & Camps",
    description:
      "Manage every accommodation listing — names, room types, descriptions, amenities, capacity, cover images and active status. Edits propagate live to the public site.",
    href: "/admin/hotels",
    icon: BedDouble,
    cta: "Open department",
    testid: "admin-dept-hotels",
  },
  {
    key: "media",
    title: "Media Library",
    description:
      "Central repository for every image on the site. Organise in folders, upload, rename, search and reuse across any page or component.",
    href: "/admin/media",
    icon: FolderOpen,
    cta: "Open department",
    testid: "admin-dept-media",
  },
  {
    key: "images",
    title: "Image directory",
    description:
      "Master view of every image rendered on the website — grouped by page and section. Search, preview, replace or reset any image. Galleries, hotel covers and pricing imagery stay in their dedicated dashboards (linked from each row).",
    href: "/admin/images",
    icon: ImagePlus,
    cta: "Open department",
    testid: "admin-dept-images",
  },
  {
    key: "galleries",
    title: "Room photo galleries",
    description:
      "Upload, reorder and replace the photos rendered on every room page. Drag-and-drop sequencing, multi-file uploads.",
    href: "/admin/galleries",
    icon: Images,
    cta: "Open department",
    testid: "admin-dept-galleries",
  },
  {
    key: "curated-galleries",
    title: "Curated Gallery",
    description:
      "Per-accommodation image collections displayed on each hotel's public gallery section. One-time clone from Bunny Storage — after that, curate freely. Deletions here never affect the original Bunny assets.",
    href: "/admin/curated-galleries",
    icon: Images,
    cta: "Open department",
    testid: "admin-dept-curated-galleries",
  },
  {
    key: "directions",
    title: "Directions & arrival info",
    description:
      "Edit per-property GPS, addresses, step-by-step directions, estimated driving times, Google Maps links, contact details and travel tips — in EN/FR/ES.",
    href: "/admin/directions",
    icon: Compass,
    cta: "Open department",
    testid: "admin-dept-directions",
  },
  {
    key: "regions",
    title: "Regions across Morocco",
    description:
      "Manage the regional groupings on the public Directions map — names, descriptions, colors, GPS centres, highlight radius and property assignments.",
    href: "/admin/regions",
    icon: Map,
    cta: "Open department",
    testid: "admin-dept-regions",
  },
  {
    key: "testimonials",
    title: "Guest testimonials",
    description:
      "Curate the per-property testimonials carousel shown on the public Directions page — add, reorder, edit or reset to the shared defaults, with EN/FR/ES copy.",
    href: "/admin/testimonials",
    icon: MessageSquareQuote,
    cta: "Open department",
    testid: "admin-dept-testimonials",
  },
  {
    key: "room-testimonials",
    title: "Room testimonials",
    description:
      "Curate the small guest quotes shown inside each room card on the hotel detail page. Add multiple quotes per room type (double, twin, suite…) — the site rotates between them on each visit.",
    href: "/admin/room-testimonials",
    icon: MessageSquareQuote,
    cta: "Open department",
    testid: "admin-dept-room-testimonials",
  },
  {
    key: "pricing",
    title: "Accommodation pricing",
    description:
      "Manage nightly rates per room across every hotel, kasbah, refuge and bivouac — capacity, characteristics and four board-basis prices in EUR. Backs the public /pricing page.",
    href: "/admin/pricing",
    icon: Tag,
    cta: "Open department",
    testid: "admin-dept-pricing",
  },
  {
    key: "widget-covers",
    title: "Widget covers",
    description:
      "Batch editor for the property photos guests see across every instance of the booking widget — carousel, dropdown, per-property blocks, itinerary strip and floating enquiry summary. Change a cover once, watch it propagate to every surface on every page. Live preview shows the exact carousel guests see.",
    href: "/admin/widget-covers",
    icon: ImagePlus,
    cta: "Open department",
    testid: "admin-dept-widget-covers",
  },
  {
    key: "text",
    title: "Text directory",
    description:
      "Master view of every text block stored in the CMS — search, edit, or reset to the brand default. Edits sync instantly across English, French and Spanish (one content block, one source of truth).",
    href: "/admin/text",
    icon: Type,
    cta: "Open department",
    testid: "admin-dept-text",
  },
  {
    key: "audio",
    title: "Audio guides",
    description:
      "Set the narration URL played by the persistent audio player when a guest taps the headphones icon on any hotel, bivouac, kasbah, riad, refuge or auberge card. Leave a row empty to fall back to the brand default.",
    href: "/admin/audio",
    icon: Headphones,
    cta: "Open department",
    testid: "admin-dept-audio",
  },
  {
    key: "bunny-storage",
    title: "Bunny Storage",
    description:
      "Browse, organise and manage every image hosted on the Bunny.net Storage Zone (xaluca). Full-screen grid with breadcrumb navigation, multi-select, drag-and-drop upload with progress bars, bulk delete, and folder creation. Copy public CDN URLs straight to the clipboard.",
    href: "/admin/bunny-storage",
    icon: Database,
    cta: "Open department",
    testid: "admin-dept-bunny-storage",
  },
  {
    key: "storage",
    title: "Storage & image optimization",
    description:
      "Live bucket stats — total bytes, % of images optimized, MB saved. One-click sweep recompresses any legacy uploads (max-width 2560, EXIF stripped) without breaking existing URLs. Safe to stop & resume.",
    href: "/admin/storage",
    icon: HardDrive,
    cta: "Open department",
    testid: "admin-dept-storage",
  },
  {
    key: "db-mirror",
    title: "Mirror production DB",
    description:
      "One-click sync to make this environment a byte-for-byte mirror of visitxaluca.com's CMS — texts, image references, galleries, pricing, testimonials, heritage, regions, directions. Customer data (bookings, contacts, chats) is never touched. Asks for confirmation before wiping local collections.",
    href: "/admin/text?mirror=auto",
    icon: CloudDownload,
    cta: "Start mirror",
    testid: "admin-dept-db-mirror",
    accent: true, // styled distinctly — this is a destructive maintenance action
  },
  {
    key: "supabase",
    title: "Supabase — Shadow migration",
    description:
      "Postgres shadow replica of the CMS content (text_slots, image_slots, galleries, media). MongoDB stays the source of truth; this panel lets you bootstrap the schema, run dry-runs, and one-click sync collections into Supabase. Fully reversible.",
    href: "/admin/supabase",
    icon: Database,
    cta: "Open department",
    testid: "admin-dept-supabase",
  },
];

const AdminHubInner = () => (
  <section className="bg-[#FDFBF7] min-h-screen" data-testid="admin-hub-page">
    <header className="px-6 md:px-10 py-10 border-b border-[#2C2621]/10 bg-white">
      <div className="max-w-[1400px] mx-auto flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-[#C16542]">Grup Xaluca · Admin</p>
          <h1 className="font-serif-x text-4xl md:text-5xl mt-2 tracking-tight">
            Admin dashboard
          </h1>
          <p className="mt-3 text-sm text-[#5C5248] max-w-2xl">
            Central control room for every department of the website. Pick the area you want to manage.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { clearAdminPassword(); window.location.reload(); }}
          className="inline-flex items-center gap-2 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase transition-colors"
          data-testid="admin-hub-logout"
        >
          <LogOut className="w-3.5 h-3.5" strokeWidth={1.6} />
          Lock
        </button>
      </div>
    </header>

    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
        {DEPARTMENTS.map((d) => {
          const Icon = d.icon;
          const isAccent = !!d.accent;
          return (
            <Link
              key={d.key}
              to={d.href}
              data-testid={d.testid}
              className={
                isAccent
                  ? "group bg-[#2C2621] text-[#FDFBF7] border border-[#C16542]/60 hover:border-[#C16542] p-8 md:p-10 flex flex-col transition-colors"
                  : "group bg-white border border-[#2C2621]/10 hover:border-[#C16542]/60 p-8 md:p-10 flex flex-col transition-colors"
              }
            >
              <span
                className={
                  isAccent
                    ? "inline-flex items-center justify-center w-12 h-12 bg-[#C16542]/20 text-[#FDFBF7] rounded-full"
                    : "inline-flex items-center justify-center w-12 h-12 bg-[#F2EBE1] text-[#A07042] rounded-full"
                }
              >
                <Icon className="w-5 h-5" strokeWidth={1.6} />
              </span>
              <h2 className="font-serif-x text-3xl md:text-4xl mt-6 tracking-tight leading-tight">
                {d.title}
              </h2>
              <p
                className={
                  isAccent
                    ? "mt-3 text-sm text-[#FDFBF7]/80 leading-relaxed"
                    : "mt-3 text-sm text-[#5C5248] leading-relaxed"
                }
              >
                {d.description}
              </p>
              <span
                className={
                  isAccent
                    ? "mt-auto pt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#FDFBF7] group-hover:gap-3 transition-all"
                    : "mt-auto pt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#C16542] group-hover:gap-3 transition-all"
                }
              >
                {d.cta}
                <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
              </span>
            </Link>
          );
        })}
      </div>

      <p className="mt-12 text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">
        More departments coming soon · F&B · Experiences · Bookings
      </p>
    </div>
  </section>
);

const AdminHubPage = () => (
  <AdminAuthGate>
    <AdminHubInner />
  </AdminAuthGate>
);

export default AdminHubPage;
